import { z } from 'zod';
import { isValidSanctionDate, sanctionDateOrderKey } from '../../shared/sanction-date-validation';

export const baseSchema = z.object({
  mas_info: z.enum(['0', '1'], { error: 'Debe indicar si necesita más información: Sí o No' }),
  // Camunda devuelve null para variables todavía sin valor. Su obligatoriedad
  // depende de la rama elegida y se comprueba en los refinements siguientes.
  accion_disiplinaria: z.string().nullish(),
  accion_disiplinaria_label: z.string().nullish(),
  tipo_falta: z.string().nullish(),
  tipo_falta_label: z.string().nullish(),
  fechaSancionInicio: z.string().nullish(),
  fechaSancionFin: z.string().nullish(),
  nuevoTexto: z.string().nullish(),
  decision_comment: z.string().nullish(),
  en_persona: z.boolean().default(false)
});

const analisislaboralSchema = baseSchema.refine(
  (data) => {
    if (data.mas_info === "0" && (!data.accion_disiplinaria || data.accion_disiplinaria.trim() === "")) {
      return false;
    }
    return true;
  },
  {
    message: "Debe seleccionar una acción disciplinaria para avanzar",
    path: ["accion_disiplinaria"],
  }
).refine(
  (data) => {
    if (data.mas_info === "0" && (!data.tipo_falta || data.tipo_falta.trim() === "")) {
      return false;
    }
    return true;
  },
  {
    message: "Debe seleccionar el tipo de falta para avanzar",
    path: ["tipo_falta"],
  }
).refine(
  (data) => {
    if (data.mas_info === "0" && data.accion_disiplinaria === "2" && (!data.fechaSancionInicio || data.fechaSancionInicio.trim() === "")) {
      return false;
    }
    return true;
  },
  {
    message: "Debe indicar la fecha de inicio de sanción",
    path: ["fechaSancionInicio"],
  }
).refine(
  (data) => {
    if (data.mas_info === "0" && data.accion_disiplinaria === "2" && (!data.fechaSancionFin || data.fechaSancionFin.trim() === "")) {
      return false;
    }
    return true;
  },
  {
    message: "Debe indicar la fecha de fin de sanción",
    path: ["fechaSancionFin"],
  }
).refine(
  (data) => {
    if (data.mas_info === "1" && (!data.nuevoTexto || data.nuevoTexto.trim() === "")) {
      return false;
    }
    return true;
  },
  {
    message: "Debe proporcionar comentarios para el coordinador cuando necesita más información",
    path: ["nuevoTexto"],
  }
).refine(
  (data) => {
    if (data.mas_info === "0" && (!data.decision_comment || data.decision_comment.trim() === "")) {
      return false;
    }
    return true;
  },
  {
    message: "Debe proporcionar comentarios sobre su análisis",
    path: ["decision_comment"],
  }
).superRefine((data, ctx) => {
  if (data.mas_info !== '0' || data.accion_disiplinaria !== '2') return;

  const start = data.fechaSancionInicio;
  const end = data.fechaSancionFin;
  const validStart = Boolean(start?.trim()) && isValidSanctionDate(start!);
  const validEnd = Boolean(end?.trim()) && isValidSanctionDate(end!);

  if (start?.trim() && !validStart) {
    ctx.addIssue({ code: 'custom', message: 'La fecha de inicio de sanción no es válida', path: ['fechaSancionInicio'] });
  }
  if (end?.trim() && !validEnd) {
    ctx.addIssue({ code: 'custom', message: 'La fecha de fin de sanción no es válida', path: ['fechaSancionFin'] });
  }
  if (validStart && validEnd && sanctionDateOrderKey(end!) < sanctionDateOrderKey(start!)) {
    ctx.addIssue({ code: 'custom', message: 'La fecha de fin no puede ser anterior a la de inicio', path: ['fechaSancionFin'] });
  }
});

export default analisislaboralSchema;

export type AnalisisLaboralFormData = z.infer<typeof analisislaboralSchema>;

export const masInfoOptions = [
  { value: "1", label: "Sí" },
  { value: "0", label: "No" }
];
